import React, { useState, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, 
  Alert, Paper
} from '@mui/material';
import { CameraAlt, Close, Check } from '@mui/icons-material';

const MobileCameraScanner = ({ open, onClose, onDataExtracted }) => {
  const [image, setImage] = useState(null);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);

  const fileInputRef = useRef(null);

  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(compressedDataUrl);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      try {
        const compressedImage = await compressImage(file);
        setImage(compressedImage);
      } catch (err) {
        console.error('Image compression failed:', err);
        // Fallback to original file
        const reader = new FileReader();
        reader.onload = (e) => setImage(e.target.result);
        reader.readAsDataURL(file);
      }
    }
  };

  const handleCameraCapture = () => {
    // For mobile, use file input with camera capture
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const extractDataFromImage = async () => {
    if (!image) return;
    
    setProcessing(true);
    setError('');
    
    try {
      // Convert base64 image to blob
      const response = await fetch(image);
      if (!response.ok) {
        throw new Error(`Failed to convert image: ${response.status}`);
      }
      
      const blob = await response.blob();
      
      // Create form data
      const formData = new FormData();
      formData.append('image', blob, 'sample-tag.jpg');
      
      // Send to backend OCR API with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);
      
      const ocrResponse = await fetch('/api/ocr/extract', {
        method: 'POST',
        body: formData,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (!ocrResponse.ok) {
        const errorText = await ocrResponse.text();
        throw new Error(`Server error ${ocrResponse.status}: ${errorText}`);
      }
      
      const result = await ocrResponse.json();
      
      if (result.success && (result.data.sampleCode || result.data.sampleName)) {
        onDataExtracted(result.data);
        handleClose();
      } else {
        setError(result.error || 'Could not extract sample information from image. Please try capturing again with better lighting or enter data manually.');
      }
    } catch (err) {
      
      if (err.name === 'AbortError') {
        setError('Request timed out. Please try again with a smaller image.');
      } else if (err.message.includes('Failed to fetch')) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(`Failed to process image: ${err.message}`);
      }
    } finally {
      setProcessing(false);
    }
  };



  const handleManualEntry = () => {
    handleClose();
    // Signal parent to show manual entry form
    onDataExtracted(null);
  };

  const handleClose = () => {
    setImage(null);
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CameraAlt />
          Scan Sample Tag
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <input
          ref={fileInputRef}
          accept="image/*"
          capture="environment"
          style={{ display: 'none' }}
          type="file"
          onChange={handleFileChange}
        />
        
        {!image ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
              📷 Sample Tag Scanner
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3 }}>
              Choose an option to get sample information:
            </Typography>
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                startIcon={<CameraAlt />}
                onClick={handleCameraCapture}
                fullWidth
                sx={{ py: 2 }}
              >
                📷 Take Photo of Tag
              </Button>
              
              <Typography variant="body2" color="textSecondary" sx={{ my: 1 }}>
                — OR —
              </Typography>
              
              <Button
                variant="outlined"
                size="large"
                onClick={handleManualEntry}
                fullWidth
                sx={{ py: 2 }}
              >
                ✏️ Enter Manually
              </Button>
            </Box>
          </Box>
        ) : (
          <Box>
            <Paper sx={{ p: 2, mb: 2 }}>
              <img 
                src={image} 
                alt="Captured" 
                style={{ width: '100%', maxHeight: 300, objectFit: 'contain' }} 
              />
            </Paper>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              Image captured! Click "Extract Data" to process the image.
            </Alert>
            
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
              Image size: {Math.round(image.length / 1024)} KB
              {Math.round(image.length / 1024) > 500 && (
                <span style={{ color: 'orange' }}> (Large - may be slow)</span>
              )}
            </Typography>
            

            
            {processing && (
              <Alert severity="info" sx={{ mb: 2 }}>
                🔄 Processing image... This may take a few seconds.
              </Alert>
            )}
          </Box>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose} startIcon={<Close />}>
          Cancel
        </Button>
        
        {image && (
          <>
            <Button 
              onClick={extractDataFromImage} 
              variant="contained" 
              startIcon={<Check />}
              disabled={processing}
            >
              {processing ? 'Processing...' : 'Extract Data'}
            </Button>
            {error && (
              <Button onClick={handleManualEntry} variant="outlined">
                Enter Manually
              </Button>
            )}
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MobileCameraScanner;